import { Checkbox, Dialog, Tabs, Theme } from '@infotech-soe/erp-ui-kit';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getPrivsState } from '../../../store/selectors/rolePrivs';
import rolePrivs from '../../../store/slices/rolePrivs';

import PrivsPane from './PrivsPane';

// 16px - checkbox, 8px - padding
const MAX_CHECKBOX_HEIGHT = 25;

const useStyles = createUseStyles(({ sizes }: Theme) => ({
  dialog: {
    maxWidth: '98vw',
  },
  checkbox: {
    height: MAX_CHECKBOX_HEIGHT,
    composes: 'flex justify-end m-py-sm',
    paddingLeft: (sizes.groupBox.padding as number[])[1],
    paddingRight: (sizes.groupBox.padding as number[])[1],
  },
  header: {
    padding: 0,
  },
  tabs: {
    height: `calc(100% - ${MAX_CHECKBOX_HEIGHT}px)`,
    padding: {
      bottom: (sizes.groupBox.padding as number[])[0],
      left: (sizes.groupBox.padding as number[])[1],
      right: (sizes.groupBox.padding as number[])[1],
    },
  },
}));

const messages = defineMessages({
  title: {
    id: 'core.rolePrivs.title',
    defaultMessage: 'Assignment of access rights',
  },
  catalog: {
    id: 'core.rolePrivs.catalog',
    defaultMessage: 'By catalogs',
  },
  subdivision: {
    id: 'core.rolePrivs.subdivision',
    defaultMessage: 'By subdivisions',
  },
  hierarchy: {
    id: 'core.rolePrivs.hierarchy',
    defaultMessage: 'By hierarchy',
  },
  all: {
    id: 'core.rolePrivs.privsAll',
    defaultMessage: 'By all',
  },
  inheritPrivs: {
    id: 'core.rolePrivs.inheritPrivs',
    defaultMessage: 'Inherit for children',
  },
});

const PrivsDialog = (): React.JSX.Element | null => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { show, panes, inherit } = useAppSelector(getPrivsState);
  const [activeTab, setActiveTab] = useState<string | undefined>();

  useEffect(() => {
    setActiveTab(panes[0]?.pane);
  }, [panes]);

  if (!show || !panes.length) {
    return null;
  }

  return (
    <Dialog
      open
      resizable
      title={formatMessage(messages.title)}
      onClose={() => dispatch(rolePrivs.close())}
      className={classes.dialog}
      noChildrenWrap
      height="90vh"
      width="90vw"
    >
      <Dialog.Content>
        <div className={`flex justify-end ${classes.checkbox}`}>
          <Checkbox
            name="inheritPrivs"
            className="col-auto"
            label={formatMessage(messages.inheritPrivs)}
            value={inherit}
            onChange={(val: boolean) =>
              dispatch(rolePrivs.toggleInheritPrivs(val))
            }
          />
        </div>
        <Tabs
          active={activeTab}
          className={classes.tabs}
          headerContainerClassName={classes.header}
          onTabActivate={setActiveTab}
        >
          {panes.map(p => (
            <PrivsPane
              mainTreeId={p.pane !== 'all' ? p.mainTreeId : undefined}
              categoryTreeId={p.categoryTreeId}
              key={p.categoryTreeId}
              name={p.pane}
              label={formatMessage(messages[p.pane])}
            />
          ))}
        </Tabs>
      </Dialog.Content>
    </Dialog>
  );
};

export default PrivsDialog;
